/**
 * Request queue to limit concurrent API requests
 * This prevents ERR_INSUFFICIENT_RESOURCES errors by controlling request rate
 */
export class RequestQueue {
  private queue: Array<() => Promise<any>> = [];
  private running = 0;
  private maxConcurrent: number;

  constructor(maxConcurrent = 3) {
    this.maxConcurrent = maxConcurrent;
  }

  async add<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.queue.push(async () => {
        try {
          const result = await fn();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      });
      this.processQueue();
    });
  }

  private async processQueue() {
    if (this.running >= this.maxConcurrent || this.queue.length === 0) {
      return;
    }

    this.running++;
    const fn = this.queue.shift();
    
    if (fn) {
      try {
        await fn();
      } catch (error) {
        console.error('Request failed:', error);
      } finally {
        this.running--;
        this.processQueue();
      }
    }
  }

  clear() {
    this.queue = [];
  }
}

// Create a singleton instance
export const apiRequestQueue = new RequestQueue(3);

/**
 * Debounce utility to prevent rapid-fire requests with proper cleanup
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): [(...args: Parameters<T>) => void, () => void] {
  let timeoutId: NodeJS.Timeout;
  
  const debouncedFn = (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
  
  const cleanup = () => {
    clearTimeout(timeoutId);
  };
  
  return [debouncedFn, cleanup];
}

/**
 * Throttle utility to limit request frequency
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}
